import { useState, useCallback, useEffect, ChangeEvent } from 'react';
import { axiosScraper, axiosDummyInstance } from '@utils/axios/axiosInstance';
import { debounce } from 'lodash';
import moment from 'moment';
import { ISimilarResponse } from '@models/scraperModel/similarModel';
import { ISearchResponse } from '@models/scraperModel/searchModel';
import { IAllMyAppListObj } from '@models/manager/service/serviceModel';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@reducers/index';
import { getAllMyAppsListInfo } from '@reducers/service/serviceThunk';
import { fetchUpdateCompetitorApp } from '@reducers/service/serviceAction';
import { gameProviderTypes } from '@configs/layout';
import ReactFlagsSelect from 'react-flags-select';
import { Countries2DigitCode } from '@models/commonModel';
import { getLocaleValueForFlag } from '@utils/common/language';

type IProps = {
  popupHandler: (popupData: IAllMyAppListObj | null) => void;
  selectedMyAppInfo: IAllMyAppListObj;
};

const CompetitorManagerPopup = ({ popupHandler, selectedMyAppInfo }: IProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const countriesArr = Countries2DigitCode.map((item) => item.code);

  // selected country
  const [selectedCountryCode, setSelectedCountryCode] = useState(getLocaleValueForFlag());

  // myAppInfoList 는 suggest App List나 search 시 해당 앱들은 다른 체크를 하기 위해서이다.
  const { myAppInfoList } = useSelector(({ serviceManager }: RootState) => serviceManager);

  // current selectedMyAppInfo State
  const [currentCompetitorList, setCurrentCompetitorList] = useState(selectedMyAppInfo?.myCompetitorAppsInfo);

  // search Text competitor's App state
  const [text, setText] = useState('');

  //appListData (suggest res| search res): 팝업으로 보여주는 리스트
  const [appList, setAppList] = useState<null | ISimilarResponse[] | ISearchResponse[]>(null);

  // suggest(similar 함수) response AppList (Scraper 요청 최소화를 위한 state)
  const [suggestAppList, setSuggestAppList] = useState<ISimilarResponse[] | null>(null);
  // search response AppList state
  const [searchAppList, setSearchAppList] = useState<ISearchResponse[] | null>(null);

  // request register CompetitorApps (경쟁자 앱 등록리스트)
  const [registerReqAppList, setRegisterReqAppList] = useState(null);

  // useEffect for initial request of SuggestAppList Result
  useEffect(() => {
    if (!!selectedMyAppInfo) {
      requestSuggestAppList(
        selectedMyAppInfo?.myAppInfo?.appId,
        selectedMyAppInfo?.myAppInfo?.providerType,
        selectedCountryCode
      );
    }
  }, []);

  useEffect(() => {
    console.log('appList: ', appList);
    console.log('currentCompetitorList: ', currentCompetitorList);
  }, [currentCompetitorList, appList]);

  useEffect(() => {
    if (text.trim().length !== 0) {
      if (!!searchAppList) {
        console.log('searchAppList: ', searchAppList);
        setAppList(searchAppList);
      }
    } else {
      if (!!suggestAppList) {
        setAppList(suggestAppList);
      }
    }
  }, [searchAppList]);

  // onFocus 함수
  // 1) 앱검색 onFocus + text 가 '' 일땐 suggest 함수 리스트 (myAppInfo 기준)
  // 2) 앱검색 onFocus + text 가 있을땐 그냥 search 함수 리스트
  const onFocusHandler = useCallback(() => {
    if (!!selectedMyAppInfo?.myAppInfo) {
      // text 가 '' 일땐 suggestList 넣기
      if (text.trim().length === 0) {
        setAppList(suggestAppList);
        // 2글자 이상일땐.. searcheAppList 넣기
      } else if (text.trim().length > 1) {
        setAppList(searchAppList);
        // 없을땐 그냥 일반 null 처리
      } else {
        setAppList(null);
      }
    }
  }, [text, suggestAppList, selectedMyAppInfo, searchAppList]);

  // onClosed popupList 함수 (similar, search 리스트 팝업 창 닫을때)
  const onClickClosed = useCallback(() => {
    setAppList(null);
    if (!!selectedMyAppInfo) {
      const { myAppInfo, myCompetitorAppsInfo } = selectedMyAppInfo;
      const reqAddAppList = currentCompetitorList.filter((currItem) => {
        return myCompetitorAppsInfo.findIndex(
          (compItem) => currItem.appId === compItem.appId && currItem.providerType === compItem.providerType
        ) > -1
          ? false
          : true;
      });
      const reqDelAppList = myCompetitorAppsInfo.filter((compItem) => {
        return currentCompetitorList.findIndex(
          (currItem) => currItem.appId === compItem.appId && currItem.providerType === compItem.providerType
        ) > -1
          ? false
          : true;
      });

      // DB에 저장 요청 처리 (추가하거나 삭제할 목록이 없을땐 요청X)
      if (!!reqAddAppList && !!reqDelAppList && !(reqAddAppList.length === 0 && reqDelAppList.length === 0)) {
        console.log('reqDelAppList: ', reqDelAppList);
        console.log('reqAddAppList: ', reqAddAppList);
        fetchUpdateCompetitorApp({
          appSeq: myAppInfo?.appSeq, //myAppInfo?.appSeq  TODO: G-STAR
          providerType: myAppInfo?.providerType,
          addCompetitorApps: reqAddAppList,
          deleteCompetitorApps: reqDelAppList,
        })
          .then((res) => {
            if (res.result) {
              return popupHandler({
                ...selectedMyAppInfo,
                myCompetitorAppsInfo: currentCompetitorList,
              });
            } else {
              alert(res?.message);
              throw new Error(res?.message);
            }
          })
          .catch((error) => console.log(error));
      }
    }
  }, [selectedMyAppInfo, popupHandler, currentCompetitorList]);

  // 1회 요청으로 한정하기 위한 state
  const [requestCount, setRequestCount] = useState(0);
  // suggest 함수 요청 함수 (특정 국가로 검색하고 에러 발생 시 다시 US 로 요청 처리할것)
  const requestSuggestAppList = useCallback(
    async (appId: string, providerType: string, countryCode: string) => {
      try {
        const { data, status } = await axiosScraper.post(`/${providerType}/similar`, {
          appId: appId,
          country: countryCode,
        });
        if (status === 200) {
          setSuggestAppList(data.map((item) => ({ ...item, providerType: item.provider_type })));
          setRequestCount(0);
        }
      } catch (error) {
        if (requestCount < 2) {
          // 해당 국가의 similar app이 없을때 default 인 'US' 로 재요청 처리
          requestSuggestAppList(appId, providerType, 'US');
          setRequestCount((prev) => prev + 1);
        } else {
          // 2회 이상일때 계속 에러 발생시 빈 배열 넣기
          setSuggestAppList([]);
          setRequestCount(0);
        }
      } finally {
        // 기존에 열린창은 닫기 처리하기
        setAppList(null);
      }
    },
    [axiosScraper, requestCount]
  );

  // search 함수 요청 함수 (feat. debounce 사용)
  const requestSearchAppList = useCallback(
    debounce((searchText: string, providerType: string, countryCode: string) => {
      axiosScraper.post(`/${providerType}/search`, { term: searchText, country: countryCode }).then((res) => {
        const { data } = res;
        console.log('data: ', data);
        setSearchAppList(data.map((item) => ({ ...item, providerType: item.provider_type })));
        setAppList(data);
      });
    }, 1000),
    [axiosScraper]
  );

  // onChange Text handler
  const onChangeTextHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const inputText = e.target.value;
      setText(inputText.trim());
      // inputText 가 '' 일땐 suggestAppList 넣기
      if (inputText.trim().length === 0) {
        setAppList(suggestAppList);
        // inputText 가 1글자 일때 null값 넣기
      } else if (inputText.trim().length === 1) {
        setAppList(null);
        // inputText 가 2글자 이상일때 searchAppList 요청하기
      } else if (inputText.trim().length > 1) {
        requestSearchAppList(inputText, selectedMyAppInfo?.myAppInfo?.providerType, selectedCountryCode ?? 'US');
      }
    },
    [requestSearchAppList, suggestAppList, selectedCountryCode]
  );

  // onClickDeleteCompetitorSingleApp Handler
  const onClickDeleteSingleCompetitorAppHandler = useCallback(
    async (appId: string, providerType: string, appSeq?: number | string) => {
      // TODO: 추루 myCompetitor App delete 요청 api로 교체 처리해야함.
      // TODO: Props 의 handler로 상위 state 변경처리

      // // 지스타용 G-STAR
      // const {data, status} = await axiosGStarInstance.delete('/app/my', {data: {appSeq: appSeq}});
      // 일반용 TODO: 내일 작업하기
      const { data, status } = await axiosDummyInstance.get('/api/commonGetResponse.json');

      if (status === 200) {
        popupHandler({
          ...selectedMyAppInfo,
          myCompetitorAppsInfo: selectedMyAppInfo.myCompetitorAppsInfo.filter((item) => {
            if (item.appId === appId && item.providerType === providerType) {
              return false;
            } else {
              return true;
            }
          }),
        });
        setCurrentCompetitorList((prev) => {
          const updateList = prev.filter((item) =>
            item.appId === appId && item.providerType === providerType ? false : true
          );
          return updateList;
        });
      }
    },
    [selectedMyAppInfo, popupHandler]
  );

  // onClickAddSingleCompetitorApp handler
  const onClickAddSingleCompetitorAppHandler = useCallback(
    async (appObj: ISimilarResponse | ISearchResponse, reqType: string) => {
      if (reqType === 'add') {
        //@ts-ignore
        setCurrentCompetitorList((prev) => {
          return [...prev, appObj];
        });
      } else {
        setCurrentCompetitorList((prev) => {
          const updateList = prev.filter((item) =>
            item.appId === appObj.appId && item.providerType === appObj.providerType ? false : true
          );
          return updateList;
        });
      }
    },
    [currentCompetitorList]
  );

  // onClose this components handler (창을 닫을 때 appManager 리스트 요청을 한다)
  const onClickClosePopup = useCallback(() => {
    dispatch(getAllMyAppsListInfo({ userId: 'sss' }));
    popupHandler(null);
  }, [dispatch, getAllMyAppsListInfo, popupHandler]);

  // onSelectCountry handler
  // (국가 변경 시 기존 창 닫고, 해당 국가로 앱리스트 다시 요청하기 - search, suggest 모두 요청)
  const onSelectCountry = useCallback(
    (code) => {
      setSelectedCountryCode(code);
      // 기존 팝업 닫기처리
      setAppList(null);
      // setSearchAppList(null);
      // suggest 요청처리(similar함수)
      requestSuggestAppList(selectedMyAppInfo?.myAppInfo?.appId, selectedMyAppInfo?.myAppInfo?.providerType, code);
      // 데이터 요청 처리 (text 가 2자 이상일때 요청처리)
      if (text.trim().length > 1) {
        requestSearchAppList(text, selectedMyAppInfo?.myAppInfo?.providerType, code);
      }
    },
    [requestSearchAppList, requestSuggestAppList, text, selectedMyAppInfo]
  );

  return (
    <>
      {!!selectedMyAppInfo && (
        <div className="lpopup-wrap">
          <div className="lpopup-content lpopup-lsmallsize">
            <h2 className="lpopup-tit">Manage your Competitor</h2>
            <div className="lpopup-container">
              <div className="competitors_search">
                <div className="search-box type-1">
                  <label>
                    <input
                      type="text"
                      className="search-ipt"
                      placeholder="Add NEW Competitors"
                      value={text}
                      onChange={onChangeTextHandler}
                      onFocus={onFocusHandler}
                    />
                  </label>
                  {/* 국가 선택 부분 */}
                  <ReactFlagsSelect
                    selected={selectedCountryCode}
                    onSelect={onSelectCountry}
                    countries={countriesArr}
                    searchable={true}
                  />
                  {/* <!-- active --> 를 넣고 빼고 차이 아래는 앱리스트 팝업 */}
                  <div className={`${!!appList ? 'active' : ''}`}>
                    <div className="dropdown_applist_bx">
                      {!!appList && text.trim().length === 0 && <div className="tit">Suggestions</div>}
                      <ul>
                        {!!appList &&
                          appList.length > 0 &&
                          appList.map((appObj) => (
                            <li key={`${appObj?.appId}_${appObj?.providerType}`}>
                              <div className="app-name">
                                <button type="button" className="app-name-click">
                                  <div className="app-name-img">
                                    <img src={appObj?.icon} alt="" />
                                  </div>
                                  <div className="app-name-txt">
                                    <h2 className="app-name-tit">{appObj?.title}</h2>
                                    <p className="app-name-dsc">{appObj?.developer}</p>
                                  </div>
                                </button>
                                {currentCompetitorList.findIndex((competitorObj) => {
                                  if (
                                    competitorObj?.appId === appObj?.appId &&
                                    competitorObj?.providerType === appObj?.providerType
                                  ) {
                                    return true;
                                  } else {
                                    return false;
                                  }
                                }) > -1 ? (
                                  <button
                                    type="button"
                                    className="dropdown_bx check"
                                    onClick={() => onClickAddSingleCompetitorAppHandler(appObj, 'del')}
                                  >
                                    <span className="material-icons">done</span>
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="dropdown_bx"
                                    onClick={() => onClickAddSingleCompetitorAppHandler(appObj, 'add')}
                                  >
                                    <span className="material-icons">add</span>
                                  </button>
                                )}
                              </div>
                            </li>
                          ))}
                      </ul>
                      <div className="search_list_close">
                        <button type="button" onClick={onClickClosed}>
                          Closed
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="lpopup-tbl-inner">
                <div className="dropdown_applist_bx">
                  <div className="tit">Added Competitor</div>
                  <ul>
                    {selectedMyAppInfo &&
                      selectedMyAppInfo?.myCompetitorAppsInfo // NEW! 를 위로 올리기 위한 sort 처리
                        .slice() // slice 로 복사 후 아래와 같이 sort를 처리해야 에러 안남
                        .sort((a, b) => moment(b.registerDate).valueOf() - moment(a?.registerDate).valueOf())
                        .map((appObj, index) => (
                          <li key={`${appObj?.appId}_${appObj?.providerType}`}>
                            <div className="app-name">
                              <button type="button" className="app-name-click">
                                <div className="app-name-img">
                                  <img src={appObj?.icon} alt="" />
                                </div>
                                <div className="app-name-txt">
                                  <h2 className="app-name-tit">{appObj?.title}</h2>
                                  <p className="app-name-dsc">
                                    {appObj?.providerType === gameProviderTypes.GooglePlayStore ? (
                                      <i className="app_icon google"></i>
                                    ) : (
                                      <i className="app_icon apple"></i>
                                    )}
                                    {appObj?.developer}
                                  </p>
                                </div>
                              </button>
                              {/* TODO: NEW! 는 오늘 등록한거 기준으로 뜨게 한다 (구현해야한다) */}
                              {moment(appObj?.registerDate).isSame(moment(new Date()), 'days') && (
                                <div className="new_tag">NEW!</div>
                              )}
                              <button
                                type="button"
                                className="dropdown_bx"
                                onClick={() =>
                                  onClickDeleteSingleCompetitorAppHandler(
                                    appObj?.appId,
                                    appObj?.providerType,
                                    appObj?.appSeq
                                  )
                                }
                              >
                                <span className="material-icons">delete</span>
                              </button>
                            </div>
                          </li>
                        ))}
                  </ul>
                </div>
              </div>
            </div>
            <button type="button" className="lpopup-close" onClick={onClickClosePopup}>
              <span className="blind">닫기</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CompetitorManagerPopup;
